/**
 * Performs a deep merge of `source` into `target`.
 * Mutates `target` only but not its objects and arrays.
 *
 * @author inspired by [jhildenbiddle](https://stackoverflow.com/a/48218209).
 */
export function mergeDeep<T, P>(target: T, source: P): T & P {
  const isObject = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === 'object';

  if (!isObject(target) || !isObject(source)) {
    return source as T & P;
  }

  const targetRecord = target as Record<string, unknown>;
  const sourceRecord = source as Record<string, unknown>;

  Object.keys(sourceRecord).forEach((key) => {
    const targetValue = targetRecord[key];
    const sourceValue = sourceRecord[key];

    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      targetRecord[key] = [...targetValue, ...sourceValue];
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      targetRecord[key] = mergeDeep(
        Object.assign({}, targetValue),
        sourceValue
      );
    } else {
      targetRecord[key] = sourceValue;
    }
  });

  return target as T & P;
}
